import { useState, useEffect, useRef } from "react";

const API = ""; // same-origin now — Vercel rewrites /api/* to the backend (see client/vercel.json). Keeps the session cookie first-party for Safari/Firefox.

// -- MONETAG REWARDED INTERSTITIAL --
// Zone + script URL exactly as given by the Monetag dashboard for this site.
const MONETAG_ZONE_ID  = "275201";
const MONETAG_SCRIPT_SRC = "https://quge5.com/88/tag.min.js";
const MONETAG_SDK_FN   = MONETAG_ZONE_ID ? `show_${MONETAG_ZONE_ID}` : null;

// Injects Monetag's SDK <script> tag once (lazily, only when a modal actually
// needs it) and resolves once window[MONETAG_SDK_FN] is ready to call.
let monetagLoadPromise = null;
function loadMonetagSdk() {
  if (!MONETAG_ZONE_ID) return Promise.reject(new Error("No Monetag zone ID configured"));
  if (monetagLoadPromise) return monetagLoadPromise;

  monetagLoadPromise = new Promise((resolve, reject) => {
    if (typeof window[MONETAG_SDK_FN] === "function") { resolve(); return; }
    const script = document.createElement("script");
    script.src   = MONETAG_SCRIPT_SRC;
    script.dataset.zone    = MONETAG_ZONE_ID;
    script.dataset.cfasync = "false";
    script.async = true;
    script.onload  = () => resolve();
    script.onerror = () => reject(new Error("Monetag SDK load failed"));
    document.head.appendChild(script);
  });
  return monetagLoadPromise;
}

/* ─────────────────────────────────────────
   WATCH AD MODAL
   Free-tier flow: user taps "Ad dekho, 1 free translation unlock karo".
   1. POST /api/ads/start  → server issues a token + minWatchSeconds
   2. Real rewarded ad plays via Monetag's SDK (show_XXX() -- a Promise that
      resolves once the user actually watches it). If no Monetag zone is
      configured yet, or the SDK fails to load / has no fill, this falls
      back to the old timed countdown so the feature never fully breaks.
   3. POST /api/ads/claim  → server verifies elapsed time + issues +1 credit

   Props:
     open       — boolean, show/hide
     token      — auth token (from localStorage in the parent)
     onClose    — called when user dismisses without claiming
     onUnlocked — called with the new adCredits count after a successful claim
─────────────────────────────────────────  */
export default function WatchAdModal({ open, token, onClose, onUnlocked }) {
  const [phase,       setPhase]       = useState("idle");   // idle | starting | watching | claiming | done | error
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [usingFallback, setUsingFallback] = useState(false); // true when showing the timed-wait fallback, not a real ad
  const [errorMsg,    setErrorMsg]    = useState("");
  const adToken   = useRef(null);
  const tickRef   = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPhase("idle");
    setErrorMsg("");
    setUsingFallback(false);
    return () => clearInterval(tickRef.current);
  }, [open]);

  if (!open) return null;

  const startWatching = async () => {
    setPhase("starting");
    setErrorMsg("");
    try {
      const res  = await fetch(`${API}/api/ads/start`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
      });
      const data = await res.json();
      if (!res.ok) { setPhase("error"); setErrorMsg(data.message || "Ad start nahi ho saka"); return; }

      adToken.current = data.token;
      const total = data.minWatchSeconds || 90; // 1:30 fallback default

      // Try a REAL rewarded ad first. If Monetag isn't configured, its SDK
      // fails to load, or it has no fill for this user/region, fall back to
      // the timed-wait so the "1 free translation" flow still works.
      setPhase("watching");
      try {
        await loadMonetagSdk();
        const showAd = window[MONETAG_SDK_FN];
        if (typeof showAd !== "function") throw new Error("Monetag SDK not ready");
        await showAd(); // resolves once the user has actually watched the ad
        setPhase("ready"); // don't auto-claim -- user must tap Claim themselves
      } catch (adErr) {
        // No real ad available -- fall back to the timed-wait placeholder.
        setUsingFallback(true);
        setSecondsLeft(total);
        clearInterval(tickRef.current);
        tickRef.current = setInterval(() => {
          setSecondsLeft(prev => {
            if (prev <= 1) {
              clearInterval(tickRef.current);
              setPhase("ready"); // don't auto-claim -- user must tap Claim themselves
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (e) {
      setPhase("error");
      setErrorMsg("Network error — dobara try karo");
    }
  };

  const claimReward = async () => {
    setPhase("claiming");
    try {
      const res  = await fetch(`${API}/api/ads/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ token: adToken.current, usingFallback }),
      });
      const data = await res.json();
      if (!res.ok) { setPhase("error"); setErrorMsg(data.message || "Claim fail ho gaya"); return; }

      setPhase("done");
      onUnlocked?.(data.adCredits);
    } catch (e) {
      setPhase("error");
      setErrorMsg("Network error — dobara try karo");
    }
  };

  return (
    <div style={S.overlay} onClick={(phase === "watching" || phase === "ready") ? undefined : onClose}>
      <div style={S.card} onClick={e => e.stopPropagation()}>
        <div style={S.iconRow}>💎</div>

        {phase === "idle" && (
          <>
            <h3 style={S.title}>1 Free Translation Unlock Karo</h3>
            <p style={S.desc}>
              Ek chhota ad dekho, aur 1 file translate karne ka credit turant mil jaayega.
              Koi payment nahi — bas 1:30 (90s) ka wait.
            </p>
            <button style={S.primaryBtn} onClick={startWatching}>▶ Ad Dekhna Shuru Karo</button>
            <button style={S.secondaryBtn} onClick={onClose}>Cancel</button>
          </>
        )}

        {phase === "starting" && (
          <p style={S.desc}>Shuru ho raha hai...</p>
        )}

        {phase === "watching" && (
          <>
            <h3 style={S.title}>{usingFallback ? "Wait Karo..." : "Ad Chal Raha Hai..."}</h3>
            {usingFallback ? (
              <>
                <div style={S.countdownCircle}>{secondsLeft}</div>
                <p style={S.desc}>Ye window band mat karo — {secondsLeft}s baaki hai.</p>
              </>
            ) : (
              <p style={S.desc}>Ad poora dekho, reward khud mil jaayega jab ad khatam ho.</p>
            )}
          </>
        )}

        {phase === "ready" && (
          <>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎁</div>
            <h3 style={S.title}>Ad Poora Ho Gaya!</h3>
            <p style={S.desc}>Neeche button dabao aur apna free translation credit claim karo.</p>
            <button style={S.primaryBtn} onClick={claimReward}>🎁 Reward Claim Karo</button>
          </>
        )}

        {phase === "claiming" && (
          <p style={S.desc}>Credit unlock ho raha hai...</p>
        )}

        {phase === "done" && (
          <>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            <h3 style={S.title}>Unlock Ho Gaya!</h3>
            <p style={S.desc}>1 free translation credit mil gaya. Ab file translate kar sakte ho.</p>
            <button style={S.primaryBtn} onClick={onClose}>Theek Hai</button>
          </>
        )}

        {phase === "error" && (
          <>
            <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
            <h3 style={S.title}>Kuch Gadbad Ho Gayi</h3>
            <p style={S.desc}>{errorMsg}</p>
            <button style={S.primaryBtn} onClick={startWatching}>🔄 Dobara Try Karo</button>
            <button style={S.secondaryBtn} onClick={onClose}>Band Karo</button>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 200, padding: 20,
  },
  card: {
    background: "#0a0a0a", border: "1px solid #FF6B0044", borderRadius: 18,
    padding: "28px 24px", width: "100%", maxWidth: 320, textAlign: "center",
    boxShadow: "0 0 40px rgba(255,107,0,0.15)",
  },
  iconRow:  { fontSize: 36, marginBottom: 8 },
  title:    { color: "#fff", fontSize: 17, fontWeight: 800, margin: "0 0 10px" },
  desc:     { color: "#999", fontSize: 13, lineHeight: 1.6, margin: "0 0 18px" },
  primaryBtn: {
    width: "100%", padding: "13px", background: "linear-gradient(135deg,#FF6B00,#e05500)",
    border: "none", borderRadius: 12, color: "#fff", fontWeight: 800, fontSize: 14,
    cursor: "pointer", marginBottom: 10,
  },
  secondaryBtn: {
    width: "100%", padding: "11px", background: "transparent", border: "1px solid #333",
    borderRadius: 12, color: "#888", fontSize: 13, cursor: "pointer",
  },
  countdownCircle: {
    width: 72, height: 72, borderRadius: "50%", border: "3px solid #FF6B00",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 26, fontWeight: 900, color: "#FF6B00", margin: "0 auto 16px",
  },
};
